'use client'

import { useState, useEffect } from 'react'
import { Loader2 } from 'lucide-react'

import RedditForm from './components/RedditForm'
import RedditResults from './components/RedditResults'
import TeaserResult, {
  type TeaserReport,
} from '@/components/tools/TeaserResult'
import { useToolUsage } from '@/lib/use-tool-usage'
import { parseToolJson } from '@/lib/parse-tool-json'
import { useToolAccessGate } from '@/components/tools/ToolAccessGate'
import { UsageLimitGate, UsageCounter } from '@/components/tools/UsageLimitGate'
import ToolRedditBrandMonitorRef from '@/components/reference/ToolRedditBrandMonitorRef'
import '@/components/reference/tool-run.css'
import '@/components/reference/tool-page.css'
import '@/components/reference/tool-live.css'

// Matches API response shape
interface RedditMonitorResult {
  brand: string
  subredditsSearched: string[]
  keywords: string[]
  checkedAt: string
  totalMentions: number
  overallScore: number
  grade: 'A' | 'B' | 'C' | 'D' | 'F'
  mentions: {
    id: string
    title: string
    body: string
    author: string
    subreddit: string
    score: number
    numComments: number
    createdUtc: number
    permalink: string
    seedingRisk: number
    riskLevel: 'low' | 'medium' | 'high'
    seedingSignals: { signal: string; points: number }[]
    gptRefined: boolean
    writingFormality?: string
    sentiment: 'positive' | 'neutral' | 'negative'
    accountAgeDays: number | null
    totalKarma: number | null
    brandDensity: number
  }[]
  subredditBreakdown: {
    subreddit: string
    mentionCount: number
    avgSentiment: string
    avgSeedingRisk: number
    topPost: { title: string; permalink: string } | null
  }[]
  scoreBreakdown: {
    category: string
    score: number
    maxScore: number
    details: string
  }[]
  seedingMatrix: {
    organic: number
    suspicious: number
    likelySeeded: number
  }
  recommendations: {
    priority: 'high' | 'medium' | 'low'
    category: string
    message: string
  }[]
  aiSummary: {
    summary: string
    actions: string[]
    context: string
  } | null
  skippedSubreddits: string[]
}

const REDDIT_LOADING_STEPS = [
  'Searching for Reddit posts...',
  'Scanning results...',
  'Analyzing mentions...',
  'Detecting seeded content...',
  'Running GPT analysis...',
  'Calculating scores...',
  'Generating AI summary...',
]

// Skeleton loader
function ResultsSkeleton() {
  const [stepIndex, setStepIndex] = useState(0)

  useEffect(() => {
    const interval = setInterval(() => {
      setStepIndex(prev =>
        prev < REDDIT_LOADING_STEPS.length - 1 ? prev + 1 : prev
      )
    }, 3500)
    return () => clearInterval(interval)
  }, [])

  const progress = Math.round(
    ((stepIndex + 1) / REDDIT_LOADING_STEPS.length) * 100
  )

  return (
    <div className='space-y-6'>
      {/* Progress indicator */}
      <div className='bg-muted/50 rounded-2xl border border-line-strong p-6'>
        <div className='flex flex-col items-center gap-5 py-4'>
          {/* Animated score ring */}
          <div className='relative w-36 h-36'>
            <svg viewBox='0 0 120 120' className='w-full h-full -rotate-90'>
              <circle
                cx='60'
                cy='60'
                r='52'
                fill='none'
                stroke='var(--border)'
                strokeWidth='8'
                opacity={0.3}
              />
              <circle
                cx='60'
                cy='60'
                r='52'
                fill='none'
                stroke='#F97316'
                strokeWidth='8'
                strokeLinecap='round'
                strokeDasharray={`${2 * Math.PI * 52}`}
                strokeDashoffset={`${2 * Math.PI * 52 * (1 - progress / 100)}`}
                opacity={0.5}
                className='transition-all duration-1000 ease-out'
              />
            </svg>
            <div className='absolute inset-0 flex flex-col items-center justify-center'>
              <Loader2
                size={24}
                className='animate-spin text-muted-foreground mb-1'
              />
              <span className='text-xs font-medium text-muted-foreground'>
                {progress}%
              </span>
            </div>
          </div>

          {/* Step indicator */}
          <div className='text-center'>
            <p className='text-sm font-medium text-foreground mb-3'>
              {REDDIT_LOADING_STEPS[stepIndex]}
            </p>
            <div className='flex items-center justify-center gap-1.5'>
              {REDDIT_LOADING_STEPS.map((_, i) => (
                <div
                  key={i}
                  className='h-1.5 rounded-full transition-all duration-500'
                  style={{
                    width: i === stepIndex ? '24px' : '6px',
                    backgroundColor:
                      i <= stepIndex ? '#F97316' : 'var(--border)',
                    opacity: i <= stepIndex ? 1 : 0.3,
                  }}
                />
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Score cards skeleton */}
      <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
        {[
          { label: 'Mention Volume', color: '#008dcc' },
          { label: 'Subreddit Diversity', color: '#8b5cf6' },
          { label: 'Sentiment', color: '#22c55e' },
          { label: 'Organic vs Seeded', color: '#F90B8A' },
          { label: 'Recency', color: '#FDC304' },
        ].map((cat, i) => {
          const active = stepIndex >= Math.min(i + 2, 5)
          return (
            <div
              key={cat.label}
              className='bg-muted/50 rounded-2xl border border-line-strong p-5'
              style={{
                opacity: active ? 1 : 0.3,
                transition: 'opacity 0.5s ease',
              }}
            >
              <div className='flex items-center justify-between mb-3'>
                <div className='flex items-center gap-2'>
                  <span
                    className='h-2 w-2 rounded-full'
                    style={{ backgroundColor: cat.color }}
                  />
                  <span className='text-sm font-semibold text-foreground/70'>
                    {cat.label}
                  </span>
                </div>
                <div className='skeleton h-4 w-12 rounded-full' />
              </div>
              <div className='skeleton h-2 w-full rounded-full mb-3' />
              <div className='space-y-1.5'>
                <div
                  className='skeleton h-3 rounded'
                  style={{ width: '65%' }}
                />
                <div
                  className='skeleton h-3 rounded'
                  style={{ width: '45%' }}
                />
              </div>
            </div>
          )
        })}
      </div>

      {/* Seeding matrix skeleton */}
      <div
        className='bg-muted/50 rounded-2xl border border-line-strong p-6'
        style={{
          opacity: stepIndex >= 3 ? 1 : 0.3,
          transition: 'opacity 0.5s ease',
        }}
      >
        <div className='skeleton h-5 w-36 rounded mb-4' />
        <div className='skeleton h-8 w-full rounded-full mb-3' />
        <div className='flex items-center justify-center gap-6'>
          {[
            { label: 'Organic', color: '#22c55e' },
            { label: 'Suspicious', color: '#FDC304' },
            { label: 'Likely Seeded', color: '#F90B8A' },
          ].map(item => (
            <div key={item.label} className='flex items-center gap-1.5'>
              <span
                className='h-2.5 w-2.5 rounded-full'
                style={{ backgroundColor: item.color, opacity: 0.5 }}
              />
              <span className='text-xs text-muted-foreground'>
                {item.label}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Mentions skeleton */}
      <div
        className='bg-muted/50 rounded-2xl border border-line-strong p-6'
        style={{
          opacity: stepIndex >= 2 ? 1 : 0.3,
          transition: 'opacity 0.5s ease',
        }}
      >
        <div className='skeleton h-5 w-32 rounded mb-4' />
        <div className='space-y-3'>
          {Array.from({ length: 3 }).map((_, i) => (
            <div
              key={i}
              className='bg-background border border-line-strong rounded-xl p-4'
            >
              <div className='flex items-center justify-between mb-2'>
                <div
                  className='skeleton h-4 rounded'
                  style={{ width: '60%' }}
                />
                <div className='skeleton h-5 w-16 rounded-full' />
              </div>
              <div className='space-y-1.5'>
                <div
                  className='skeleton h-3 rounded'
                  style={{ width: '90%' }}
                />
                <div
                  className='skeleton h-3 rounded'
                  style={{ width: '70%' }}
                />
              </div>
              <div className='flex items-center gap-3 mt-3'>
                <div className='skeleton h-3 w-20 rounded' />
                <div className='skeleton h-3 w-16 rounded' />
                <div className='skeleton h-3 w-12 rounded' />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

export default function RedditBrandMonitorPage() {
  const [result, setResult] = useState<RedditMonitorResult | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const toolUsage = useToolUsage('reddit-monitor')
  const gate = useToolAccessGate('reddit-monitor', 'Reddit Brand Monitor', {
    variant: 'reference',
  })

  async function handleSubmit(
    brand: string,
    subreddits: string,
    keywords: string
  ) {
    if (!gate.ready) {
      // Resumes after the code verifies, so the typed brand runs (PIX-47).
      gate.openGate(brand, () => void runCheck(brand, subreddits, keywords))
      return
    }

    await runCheck(brand, subreddits, keywords)
  }

  async function runCheck(brand: string, subreddits: string, keywords: string) {
    setIsLoading(true)
    setError(null)
    setResult(null)

    try {
      const response = await toolUsage.fetchWithUsage(
        '/api/tools/reddit-monitor',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ brand, subreddits, keywords }),
        }
      )

      const data = await parseToolJson(response)

      if (
        response.status === 403 &&
        (data.error === 'usage_limit' || data.error === 'gate_required')
      ) {
        gate.openGate(brand, () => void runCheck(brand, subreddits, keywords))
        return
      }

      if (response.status === 429 && data.error === 'domain_cap') {
        gate.showDomainCap(data)
        return
      }

      if (!response.ok) {
        throw new Error(
          data.message || 'Reddit monitoring failed. Please try again.'
        )
      }

      setResult(data as RedditMonitorResult)
      gate.markConsumed()

      // Scroll to results
      setTimeout(() => {
        document
          .getElementById('reddit-results')
          ?.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }, 100)
    } catch (err) {
      const message =
        err instanceof Error
          ? err.message
          : 'Something went wrong. Please try again.'
      setError(message)
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className='shell pg-tool pg-tool-reddit-brand-monitor'>
      {gate.modal}
      <ToolRedditBrandMonitorRef
        form={
          <>
            <RedditForm
              variant='reference'
              onSubmit={handleSubmit}
              isLoading={isLoading}
            />
            {error && (
              <p role='alert' className='run__note run__error tool-live__after'>
                <i />
                {error}
              </p>
            )}
          </>
        }
        output={
          <>
            <div id='reddit-results' className='max-w-4xl mx-auto'>
              {isLoading && <ResultsSkeleton />}
              {toolUsage.isGated && !isLoading && (
                <UsageLimitGate
                  variant='reference'
                  toolName='Reddit Brand Monitor'
                  used={toolUsage.used}
                  limit={toolUsage.limit}
                />
              )}
              {result && !isLoading && !toolUsage.isGated && (
                <>
                  {(result as unknown as { access?: { tier?: string } }).access
                    ?.tier === 'teaser' ? (
                    <TeaserResult
                      variant='reference'
                      report={
                        (result as unknown as { report: TeaserReport }).report
                      }
                      toolName='Reddit Brand Monitor'
                    />
                  ) : (
                    <RedditResults result={result} />
                  )}
                  <UsageCounter
                    variant='reference'
                    used={toolUsage.used}
                    limit={toolUsage.limit}
                    isTokenHolder={toolUsage.isTokenHolder}
                  />
                </>
              )}
            </div>
          </>
        }
      />
    </div>
  )
}
